import { MdsIconKind } from "@/design-system/components/icon";
import { MdsIcon } from "@/design-system/components/icon";
import { MDSButton, MDSListBox } from "@/mds";
import { MDSListBoxItemProps } from "@/mds/components/lists/list-box-item";
import { IntegrationsPageContentForm } from "@/pages/integrations/shared/integrations-form";
import { DeleteItemModal } from "@/pages/integrations/shared/integrations-delete-modal";
import { useAppStore } from "@/store";
import { EmailSenderObservable } from "@/store/email-senders/EmailSenderObservable";
import styled from "@emotion/styled";
import { observer } from "mobx-react-lite";
import { FC, useState } from "react";
import { TextMessageSenderObservable } from "@/store/text-message-senders/TextMessageSenderObservable";
import { IntegrationItemKind } from "@/pages/integrations/integrations-page.types";
import { IntegrationsFormatting } from "@/pages/integrations/shared/integrations-formatting";
import { StyledContentListText } from "@/pages/integrations/shared/integrations.styles";

type Props = {
  itemKind: IntegrationItemKind;
};

type IntegrationItemType = EmailSenderObservable | TextMessageSenderObservable;

export const IntegrationsSenderItemList: FC<Props> = observer(({ itemKind }) => {
  const { store } = useAppStore();
  const [itemToDelete, setItemToDelete] = useState<IntegrationItemType | null>(null);

  const integrationStore = itemKind === "email" ? store.emailSenders : store.textMessageSenders;

  const { unVerifiedItem, addUnverifiedItem } = integrationStore;
  const label = itemKind === "email" ? "Verified Emails" : "Verified Phone Numbers";

  const { items } = getItems({
    data: integrationStore.items,
    setItemToDelete,
    unVerifiedItem,
    addUnverifiedItem,
    itemKind,
  });

  return (
    <Wrapper listHasItems={items.length > 0}>
      <MDSListBox label={label} items={items} />
      <DeleteItemModal itemToDelete={itemToDelete} setItemToDelete={setItemToDelete} />

      {!!unVerifiedItem && <IntegrationsPageContentForm unVerifiedItem={unVerifiedItem} />}
      <IntegrationsFormatting itemKind={itemKind} />
    </Wrapper>
  );
});

const getItems = ({
  data,
  setItemToDelete,
  unVerifiedItem,
  addUnverifiedItem,
  itemKind,
}: {
  data: IntegrationItemType[];
  setItemToDelete: (item: IntegrationItemType) => void;
  unVerifiedItem: IntegrationItemType | undefined;
  addUnverifiedItem: () => void;
  itemKind: IntegrationItemKind;
}) => {
  const isFormOpen = !!unVerifiedItem;
  const label = itemKind === "email" ? "email" : "phone number";
  const iconKind = itemKind === "email" ? MdsIconKind.UserSolid : MdsIconKind.Phone;

  const list: IntegrationItemType[] = data.filter(item => item.id !== unVerifiedItem?.id);

  const items: MDSListBoxItemProps[] = list.map(info => ({
    id: info.id,
    textValue: info.input,
    leftAddon: (
      <UserIconWrapper>
        <MdsIcon kind={iconKind} />
      </UserIconWrapper>
    ),
    children: (
      <StyledContentListText>
        {info.input} <MdsIcon kind={MdsIconKind.CheckCircleSolid} />
      </StyledContentListText>
    ),
    rightAddon: info.canDelete ? (
      <MDSButton
        color="transparent"
        onPress={() => setItemToDelete(info)}
        aria-label={`Delete ${label}`}
      >
        <MdsIcon kind={MdsIconKind.Trash} />
      </MDSButton>
    ) : null,
  }));

  if (!isFormOpen) {
    items.push({
      id: "create-integration-sender",
      textValue: `Add new ${label}`,
      onAction: addUnverifiedItem,
      leftAddon: (
        <IconWrapper>
          <MdsIcon kind={MdsIconKind.Plus} />
        </IconWrapper>
      ),
      children: <AddButtonText>Add {label}</AddButtonText>,
    });
  }

  return { items };
};

const Wrapper = styled.div<{ listHasItems: boolean }>(({ theme, listHasItems }) => ({
  width: "100%",
  marginTop: theme.spacing.sm,
  padding: 0,
  display: "flex",
  flexDirection: "column",
  gap: listHasItems ? theme.spacing.sm : theme.spacing.xs,
}));

const UserIconWrapper = styled.div(({ theme }) => ({
  height: 36,
  width: 36,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.borderRadius.mediumLarge,
  backgroundColor: theme.colors.grey.x50,

  "& svg": {
    color: theme.colors.grey.x400,
  },
}));

const IconWrapper = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: theme.borderRadius.mediumLarge,

  height: 36,
  width: 36,

  "& svg": {
    height: 20,
    width: 20,
    color: theme.colors.grey.x400,
  },
}));

const AddButtonText = styled.div(({ theme }) => ({
  fontWeight: theme.fontWeights.medium,
  color: theme.colors.grey.x600,
}));
